import { useEffect, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { getListCustomer } from "../../services";
import { CustomerResponse } from "../../types/customer.type";
import { useDebounce } from "../../utils/hooks/useDebounce";

export interface CustomerOption {
  uuid: string;
  customer_code: string;
  name: string;
  status?: string | null;
}

interface CustomerPickerProps {
  value: CustomerOption | null;
  onChange: (customer: CustomerOption | null) => void;
  /** Head office names the branch to search in; a branch user searches its own. */
  branchUuid?: string;
  disabled?: boolean;
  error?: string;
  helperText?: string;
}

const SEARCH_LIMIT = 20;

/** Shops the server refuses an order for (disabled / suspended), shown but not pickable. */
const isBlocked = (status?: string | null) => status === "disable" || status === "suspend";

/** A code has digits and no spaces (e.g. Z1030033); anything else is searched as a name. */
const looksLikeCode = (text: string) => /\d/.test(text) && !/\s/.test(text);

/**
 * Customer search for the special-order form, over the same GET /customers the customer list
 * uses. The typed text goes to `customer_code` when it looks like one, otherwise to `name`.
 */
export default function CustomerPicker({
  value,
  onChange,
  branchUuid,
  disabled,
  error,
  helperText,
}: CustomerPickerProps) {
  const [input, setInput] = useState<string>("");
  const [options, setOptions] = useState<CustomerOption[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const debouncedInput = useDebounce(input, 400);

  useEffect(() => {
    const text = debouncedInput.trim();
    // Typing into the box after a pick echoes the picked label; that is not a new search.
    const pickedLabel = value ? `${value.customer_code} ${value.name}` : "";
    if (disabled || text.length < 2 || text === pickedLabel) {
      setOptions(value ? [value] : []);
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);
    (async () => {
      try {
        const {
          data: { data },
        } = await getListCustomer({
          page: 1,
          limit: SEARCH_LIMIT,
          ...(looksLikeCode(text) ? { customer_code: text } : { name: text }),
          branch_uuid: branchUuid || undefined,
        });
        if (!active) return;
        const rows: CustomerResponse[] = data?.rows ?? [];
        setOptions(
          rows.map((row) => ({
            uuid: row.uuid,
            customer_code: row.customer_code,
            name: row.name,
            status: row.status,
          })),
        );
      } catch {
        if (active) setOptions([]);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [debouncedInput, branchUuid, disabled, value]);

  return (
    <Autocomplete<CustomerOption, false, false, false>
      value={value}
      options={options}
      disabled={disabled}
      loading={loading}
      loadingText="กำลังค้นหาลูกค้า..."
      filterOptions={(list) => list}
      getOptionLabel={(option) => `${option.customer_code} ${option.name}`}
      isOptionEqualToValue={(option, selected) => option.uuid === selected.uuid}
      getOptionDisabled={(option) => isBlocked(option.status)}
      onChange={(_, selected) => onChange(selected)}
      onInputChange={(_, text) => setInput(text)}
      noOptionsText={
        input.trim().length < 2 ? "พิมพ์ชื่อหรือรหัสลูกค้าอย่างน้อย 2 ตัวอักษร" : "ไม่พบลูกค้า"
      }
      renderOption={(props, option) => {
        const { key, ...rest } = props as typeof props & { key: string };
        return (
          <Box component="li" key={key} {...rest}>
            <Box>
              <Typography variant="body2" fontWeight={600}>
                {option.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {option.customer_code}
                {isBlocked(option.status)
                  ? option.status === "suspend"
                    ? " · พักใช้งาน"
                    : " · ไม่เปิดใช้งาน"
                  : ""}
              </Typography>
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          label="ลูกค้า"
          placeholder="ค้นหาชื่อหรือรหัสลูกค้า"
          error={Boolean(error)}
          helperText={error || helperText}
        />
      )}
    />
  );
}
